<template>
    <div>
        <el-card v-loading="loading">
            <div slot="header" class="clearfix">
                <span>Update Source Data</span>
            </div>
            <div class="box-content">
                <el-form :rules="rules" :model="form" ref="uploadSourceDataForm" label-width="200px"
                         label-position="left" @submit.prevent="submitSource()">
                    <el-row :gutter="20" class="main-row">
                        <el-col :span="13">
                            <el-form-item label="Choose Source Type">
                                <el-select v-model="form.source_type" @change="form.source_slug=''">
                                    <el-option
                                            v-for="item in sourceType"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value">
                                    </el-option>
                                </el-select>
                            </el-form-item>
                            <el-form-item label="Choose File Type">
                                <el-switch
                                        v-model="fileType"
                                        active-text="CSV"
                                        inactive-text="XLSX"
                                        @change="resetForm"
                                >
                                </el-switch>
                            </el-form-item>
                            <el-collapse-transition>
                                <div v-show="fileType">
                                    <el-form-item v-if="Object.keys(sourceData).length" label="Select a source">
                                        <el-select filterable default-first-option :filter-method="filterSourceSelect"
                                                   v-model="form.source_slug"
                                                   clearable
                                                   placeholder="please select a source">
                                            <el-option v-for="abbr in sourceSelect"
                                                       :label="abbr"
                                                       :value="abbr"
                                                       :key="abbr">
                                                <span class="source-abbr" v-text="abbr"></span>
                                                <span class="source-name" v-text="sourceData[abbr]['name']"></span>
                                            </el-option>
                                        </el-select>
                                        <el-button @click="openAddNewSourceDialog" class="add-new-source-btn"
                                                   icon="el-icon-circle-plus-outline"></el-button>
                                        <div v-if="form.source_slug" class="selected-source"
                                             v-text="sourceData[form.source_slug]['name']"></div>
                                    </el-form-item>
                                    <el-form-item label="Select Data Date">
                                        <el-date-picker
                                                type="date"
                                                placeholder="Select a date"
                                                format="yyyy-MM-dd"
                                                value-format="yyyy-MM-dd"
                                                v-model="form.data_date"></el-date-picker>
                                    </el-form-item>
                                    <el-collapse-transition>
                                        <div v-show="form.source_type=='quant'">
                                            <el-form-item
                                                    label="Lower Bound"
                                                    prop="lower_bound">
                                                <el-input
                                                        placeholder="Enter Lower Bound"
                                                        v-model="form.lower_bound"></el-input>
                                            </el-form-item>
                                            <el-form-item
                                                    label="Upper Bound"
                                                    prop="upper_bound">
                                                <el-input
                                                        placeholder="Enter Upper Bound"
                                                        v-model="form.upper_bound"></el-input>
                                            </el-form-item>
                                        </div>
                                    </el-collapse-transition>
                                </div>
                            </el-collapse-transition>
                            <el-form-item label="Choose a File">
                                <el-upload
                                        class="upload-file"
                                        :action="action"
                                        ref="fileUpload"
                                        :auto-upload="false"
                                        :limit="1"
                                        :headers="$headers"
                                        :data="form"
                                        :on-success="onUploadSuccess"
                                        :on-error="onUploadError"
                                        :on-exceed="handleExceed"
                                        :before-upload="beforeFileUpload">
                                    <el-button size="small" type="primary">Choose a <span
                                            v-text="fileTypeText.toLowerCase()"></span> file
                                    </el-button>
                                    <span slot="tip" class="el-upload__tip"
                                          v-text="`(${fileTypeText} file with size less than 500kb)`"></span>
                                </el-upload>
                            </el-form-item>
                        </el-col>
                        <el-col :span="11" class="help-text">
                            <h4>
                                <i class="el-icon-info"></i>&nbsp;<span v-text="fileTypeText"></span>
                                upload requirements
                                <el-link class="link" :underline="false"
                                         :href="`/downloads/upload-${fileTypeText.toLowerCase()}-template-${form.source_type.toLowerCase()}.${fileTypeText.toLowerCase()}?version=1.03`"
                                         icon="el-icon-download" type="primary"> Download <span
                                        v-text="fileTypeText"></span> Template
                                </el-link>
                            </h4>
                            <div v-show="fileType && form.source_type=='quant'">
                                <p>No header needed</p>
                                <p>Rows must be in the format <small>"USA,40"</small></p>
                                <p>Country code must be Alpha 3. For example, <small>"USA"</small></p>
                                <p>Score must be within the upper and lower risk values</p>
                                <p>For a source, Upper and Lower Bound both can be empty but only one bound can not be
                                    left empty</p>
                                <p>When both lower and upper bound are empty it takes previous limit bounds</p>
                            </div>
                            <div v-show="!fileType && form.source_type=='quant'">
                                <p>The first, second and third row must be a <small>Lower Bound, Upper Bound and Country
                                    Code</small> respectively</p>
                                <p>For a source, Upper and Lower Bound both can be empty, When lower and upper bound are
                                    empty it takes previous limit bounds</p>
                                <p>Header of the second and subsequent columns should be the source abbreviations. For
                                    example, <small>"CATO-HFI"</small></p>
                                <p>Source abbreviation in header is case sensitive</p>
                                <p>Country code must be Alpha 3. For example, <small>"USA"</small></p>
                                <p>Score must be within the upper and lower risk values</p>
                                <p>Sheet name must be in the format <em>YYYY-MM-DD</em>. For example, <small>"2019-12-19"</small>,
                                    otherwise the sheet will be ignored</p>
                                <p>If the entered source abbreviation is invalid, data related to that source will be
                                    ignored</p>
                            </div>
                            <div v-show="fileType && form.source_type=='qual'">
                                <p>No header needed</p>
                                <p>Rows must be in the format <small>"USA,40"</small></p>
                                <p>Country code must be Alpha 3. For example, <small>"USA"</small></p>
                                <p>Values must be acceptable values e.g. <small>red</small>, <small>yellow</small>, <small>green</small> <em> (not case sensitive)</em></p>
                            </div>
                            <div v-show="!fileType && form.source_type=='qual'">
                                <p>Header of the second and subsequent columns should be the source abbreviations. For
                                    example, <small>"CATO-HFI"</small></p>
                                <p>Source abbreviation in header is case sensitive</p>
                                <p>Country code must be Alpha 3. For example, <small>"USA"</small></p>
                                <p>Sheet name must be in the format <em>YYYY-MM-DD</em>. For example, <small>"2019-12-19"</small>,
                                    otherwise the sheet will be ignored</p>
                                <p>If the entered source abbreviation is invalid, data related to that source will be
                                    ignored</p>
                                <p>Values must be acceptable values e.g. <small>red</small>, <small>yellow</small>, <small>green</small> <em> (not case sensitive)</em></p>
                            </div>
                        </el-col>
                    </el-row>
                    <el-form-item class="text-right">
                        <el-button :disabled="formValidationFailed" size="small" type="primary" @click="submitSource">
                            Submit
                            Upload <i class="el-icon-upload"></i></el-button>
                    </el-form-item>
                </el-form>
            </div>
        </el-card>
        <add-new-source-dialog ref="addNewSourceDialog"
                               v-bind:loading.sync="loading"
                               :set-source-type="form.source_type"
                               v-on:selectSource="selectSource">
        </add-new-source-dialog>
    </div>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';
    import AddNewSourceDialog from './../components/AddNewSourceDialog.vue';

    const {mapState, mapActions} = createNamespacedHelpers('riskIndex');

    export default {
        name:        "AddDataForm",
        components:  {AddNewSourceDialog},
        data() {
            let checkUpperBound = (rule, value, callback) => {
                if (this.form.lower_bound != '' && value == '') {
                    return callback(new Error('Please enter a Upper Bound'));
                } else if (isNaN(value)) {
                    return callback(new Error('Please enter a number'));
                }
                callback();
            };
            let checkLowerBound = (rule, value, callback) => {
                if (this.form.upper_bound != '' && value == '') {
                    return callback(new Error('Please enter a Upper Bound'));
                } else if (isNaN(value)) {
                    return callback(new Error('Please enter a number'));
                }
                callback();
            };
            return {
                loading:      true,
                sourceSelect: [],
                action:       '',
                fileType:     true,  //
                sourceType:   [
                    {
                        value: 'quant',
                        label: 'Quantitative'
                    }, {
                        value: 'qual',
                        label: 'Qualitative'
                    }
                ],
                form:         {
                    source_slug: '',
                    source_id:   '',
                    data_date:   '',
                    lower_bound: '',
                    upper_bound: '',
                    source_type: 'quant'
                },
                rules:        {
                    'lower_bound': [
                        {validator: checkLowerBound, trigger: 'blur'}
                    ],
                    'upper_bound': [
                        {validator: checkUpperBound, trigger: 'blur'}
                    ]
                }
            };
        },
        methods:     {
            ...mapActions([
                'refreshRiskIndexTableData', 'loadSourceData'
            ]),
            beforeFileUpload:       function (file) {
                let self                   = this;
                const isCSV                = file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
                const isXLSX               = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                let validFileType          = self.fileType ? isCSV : isXLSX;
                const isUnderFileSizeLimit = file.size < 500 * 1024;
                if (!validFileType) {
                    self.$notify.error({
                        title:   'Error',
                        message: `File must be ${self.fileTypeText} format!`
                    });
                }
                if (!isUnderFileSizeLimit) {
                    self.$nextTick(function () {
                        self.$notify.error({
                            title:   'Error',
                            message: 'File size can not exceed 500KB!'
                        });
                    });
                }
                this.loading = false;
                return validFileType && isUnderFileSizeLimit;
            },
            handleExceed:           function () {
                this.$notify.error({
                    title:   'Error',
                    message: 'The limit is 1 file, you must remove a file before choosing another.'
                });
            },
            submitSource:           async function () {
                let validated = await this.$refs.uploadSourceDataForm.validate().then(() => true).catch(() => false);
                if (!validated) {
                    this.$notify.error({
                        title:   'Error',
                        message: 'Please correct errors in the Form!'
                    });
                    return;
                }
                this.loading = true;
                this.$refs.fileUpload.submit();
            },
            onUploadSuccess:        async function (resp) {
                this.resetForm();
                await this.loadSourceData(true);
                await this.refreshRiskIndexTableData(true);
                this.$notify.success({
                    title:   'Success',
                    message: `${resp.data.length} Source Data has been uploaded successfully.`
                });
                this.loading = false;
            },
            onUploadError:          function (err) {
                let message = 'Source Data update has been failed.';
                try {
                    message = JSON.parse(err['status_message']).reason;
                } catch (e) {
                    console.error(e);
                }
                this.$notify.error({
                    title:   'Error',
                    message: message
                });
                this.loading = false;
            },
            openAddNewSourceDialog: function () {
                this.$refs.addNewSourceDialog.showAddNewSourceDialog();
            },
            selectSource:           function (sourceAbbreviation) {
                this.loading          = false;
                this.form.source_slug = sourceAbbreviation;
            },
            filterSourceSelect:     function (query) {
                let re            = new RegExp(query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), "ig");
                let filtered      = Object.keys(this.sourceData).filter(x => {
                    re.lastIndex = 0;
                    return re.test(x + this.sourceData[x]['name']) && this.sourceData[x]['type'] == this.form.source_type;
                });
                this.sourceSelect = filtered.sort();
            },
            resetForm() {
                this.$refs.fileUpload.clearFiles();
                this.form.source_slug = '';
                this.form.data_date   = '';
                this.$refs.uploadSourceDataForm.resetFields();
            }
        }, computed: {
            ...mapState([
                'sourceData'
            ]),
            formValidationFailed: function () {
                let checkCSVFile  = this.fileType && this.form.source_slug && this.form.data_date && this.$refs.fileUpload.uploadFiles.length;
                let checkXLSXFile = !this.fileType && this.$refs.fileUpload.uploadFiles.length;
                return !(checkCSVFile || checkXLSXFile);
            },
            fileTypeText:         function () {
                return this.fileType ? 'CSV' : 'XLSX';
            }
        },
        mounted() {
            this.action = this.$appConfig.url.riskIndex + "upload-source-data";
            if (Object.keys(this.sourceData).length) {
                this.sourceSelect = Object.keys(this.sourceData).sort();
                this.loading      = false;
            }

        },
        watch:       {
            sourceData:         {
                handler: function () {
                    this.loading      = false;
                    let filtered      = Object.keys(this.sourceData).filter(x => {
                        return this.sourceData[x]['type'] == this.form.source_type;
                    });
                    this.sourceSelect = filtered.sort();
                },
                deep:    true
            },
            'form.source_slug': function (val) {
                this.form.source_id = val ? this.sourceData[val].id : '';
            },
            'form.source_type': function (val) {
                let filtered = Object.keys(this.sourceData).filter(x => {
                    return this.sourceData[x]['type'] == val;
                });

                this.sourceSelect = filtered;
            }
        }
    };
</script>

<style scoped lang="scss">
    @import "@/styles/_base.scss";

    .custom-select {
        height: 2.3em;
        font-size: 1.3em;
        background: #fff;
        color: #5b5747;
        border: none;
    }

    .source-abbr {
        width: 100px;
        float: left;
    }

    .selected-source {
        color: $color-grey-dark;
        font-size: 11px;
        line-height: 15px;
        padding: 5px 0;
    }

    .source-name {
        width: 350px;
        text-align: left;
        padding: 0 15px;
        float: right;
        color: $color-grey-dark;
        font-size: 11px;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .grey-text {
        color: $color-grey-dark;
        padding-left: 10px;
        font-size: 12px;
    }

    .upload-file {
        max-width: 350px;
    }

    .el-upload__tip {
        color: $color-grey-dark;
        padding-left: 10px;
    }

    .text-right {
        text-align: right;
    }

    .add-new-source-btn {
        margin-left: 10px;
        padding: 0;
        background: transparent;
        border: none;
        outline: none;
        cursor: pointer;
        font-size: 22px;
        position: relative;
        top: 4px;
    }

    .help-text {
        h4 {
            color: $color-blue;
            padding-bottom: 5px;
            margin: 0 0 5px;
            border-bottom: 1px solid #EBEEF5;

            .link {
                padding: 1px;
                float: right;
            }
        }

        p {
            color: $color-grey-dark;
            font-size: 12px;
            cursor: pointer;
            opacity: .5;
            transition: opacity .2s ease-in-out;
            list-style: disc outside none;
            display: list-item;
            margin: 5px 5px 5px 15px;

            small {
                font-weight: bold;
            }

            &:hover {
                opacity: 1;

                small {
                    color: $color-blue;
                }
            }
        }
    }

    .main-row {
        min-height: 250px;

        &::v-deep {
            .el-input {
                width: 250px;
            }

        }
    }
</style>